import { Download, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { BPlotModal } from '../../../components/BPlotModal';
import { CompletionScreen } from '../../../components/CompletionScreen';
import { ComponentErrorBoundary } from '../../../components/ErrorBoundary';
import { NarrativeEditor } from '../../../components/NarrativeEditor';
import { NarrativePreview } from '../../../components/NarrativePreview';
import { SettingsPopup } from '../../../components/SettingsPopup';
import { UnifiedBottomBar } from '../../../components/UnifiedBottomBar';
import { UnifiedTopBar } from '../../../components/UnifiedTopBar';
import { ViewSwitcher } from '../../../components/ViewSwitcher';
import { WordLimitWarningModal } from '../../../components/WordLimitWarningModal';
import { WorldStateModal } from '../../../components/WorldStateModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useNodeHistory } from '../../../hooks/useNodeHistory';
import {
  useClearAllState,
  useClickedChoiceIndex,
  useCurrentProjectId,
  useHasBPlotBeenCompleted,
  useHasSeenBPlotIntro,
  useNarrativeState,
  usePlanningState,
  useProjects,
  useSelectedTab,
  useSetClickedChoiceIndex,
  useSetCurrentNode,
  useSetCurrentSegment,
  useSetHasBPlotBeenCompleted,
  useSetHasSeenBPlotIntro,
  // usePendingWorldChoice,
  useSetPendingWorldChoice,
  useSetSelectedTab,
  useSetShowBPlotModal,
  useSetShowSettingsPopup,
  useSetShowWordLimitWarning,
  useSetShowWorldStateModal,
  useShowBPlotModal,
  useShowSettingsPopup,
  useShowWordLimitWarning,
  useShowWorldStateModal,
  useUndoRedo,
  useUpdateNode,
} from '../../../store/hooks';
import { SegmentId } from '../../../types/narrative';
import { downloadFile, exportToJson, exportToMarkdown } from '../../../utils/exportHelpers';
import {
  clearNodeContent,
  getSegmentProgress,
  getSegmentStartNode,
  getWorldFromNodeId,
  isSegmentComplete,
} from '../../../utils/narrativeHelpers';
import {
  canNavigateFromCurrentNode,
  getCurrentNodeWordCount,
} from '../../../utils/wordLimitHelpers';

export default function ABA() {
  // Zustand store hooks
  const narrativeState = useNarrativeState();
  const planningState = usePlanningState();
  const updateNode = useUpdateNode();
  const setCurrentNode = useSetCurrentNode();
  const setCurrentSegment = useSetCurrentSegment();
  const showSettingsPopup = useShowSettingsPopup();
  const setShowSettingsPopup = useSetShowSettingsPopup();
  const showBPlotModal = useShowBPlotModal();
  const setShowBPlotModal = useSetShowBPlotModal();
  const hasBPlotBeenCompleted = useHasBPlotBeenCompleted();
  const setHasBPlotBeenCompleted = useSetHasBPlotBeenCompleted();
  const hasSeenBPlotIntro = useHasSeenBPlotIntro();
  const setHasSeenBPlotIntro = useSetHasSeenBPlotIntro();
  const clearAllState = useClearAllState();
  const { undo, redo, canUndo, canRedo } = useUndoRedo();
  const setSelectedTab = useSetSelectedTab();
  const showWordLimitWarning = useShowWordLimitWarning();
  const setShowWordLimitWarning = useSetShowWordLimitWarning();
  const selectedTab = useSelectedTab();
  const showWorldStateModal = useShowWorldStateModal();
  const setShowWorldStateModal = useSetShowWorldStateModal();
  // const pendingWorldChoice = usePendingWorldChoice();
  const setPendingWorldChoice = useSetPendingWorldChoice();
  const clickedChoiceIndex = useClickedChoiceIndex();
  const setClickedChoiceIndex = useSetClickedChoiceIndex();
  const { user } = useAuth();
  const projects = useProjects();
  const currentProjectId = useCurrentProjectId();

  // Local state
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [hasShownCompletion, setHasShownCompletion] = useState(false);

  // Local state for pending navigation
  const [pendingNodeId, setPendingNodeId] = useState<string | null>(null);

  const {
    visit: visitNode,
    back: historyBack,
    forward: historyForward,
    canBack: canHistoryBack,
    canForward: canHistoryForward,
  } = useNodeHistory(narrativeState?.currentNodeId || 'S1_Opening_Hook');

  // Check if B-Plot data exists on mount
  useEffect(() => {
    const hasData =
      planningState.bPlotReason && planningState.bPlotNature && planningState.bPlotExplanation;
    if (hasData && !hasBPlotBeenCompleted) {
      // Batch these updates to prevent multiple re-renders
      requestAnimationFrame(() => {
        setHasBPlotBeenCompleted(true);
        setHasSeenBPlotIntro(true); // If they have data, they've seen the intro
      });
    }
  }, [
    planningState.bPlotReason,
    planningState.bPlotNature,
    planningState.bPlotExplanation,
    hasBPlotBeenCompleted,
    setHasBPlotBeenCompleted,
    setHasSeenBPlotIntro,
  ]);

  // Handle segment changes
  const handleSegmentChange = useCallback(
    (segmentId: SegmentId) => {
      // Show B-Plot modal when entering Segment 2 from any direction
      if (
        segmentId === 2 &&
        narrativeState.currentSegmentId !== 2 &&
        isSegmentComplete(narrativeState, '1') &&
        !hasBPlotBeenCompleted
      ) {
        setShowBPlotModal(true);
        return; // Don't change segment yet
      }

      const startNode = getSegmentStartNode(segmentId);

      // Batch navigation updates to prevent race conditions
      requestAnimationFrame(() => {
        setCurrentNode(startNode);
        setCurrentSegment(segmentId);
        visitNode(startNode);
      });
    },
    [
      hasBPlotBeenCompleted,
      narrativeState,
      setCurrentSegment,
      setCurrentNode,
      visitNode,
      setShowBPlotModal,
    ]
  );

  // Handle node navigation
  const handleNodeNavigation = useCallback(
    (nodeId: string) => {
      // Check word limits before allowing navigation
      if (!canNavigateFromCurrentNode(narrativeState)) {
        setShowWordLimitWarning(true);
        return;
      }

      // Get the target node to check its segment
      const targetNode = narrativeState?.nodes?.[nodeId];

      // Check if navigating to a Segment 2 node from another segment
      if (
        targetNode &&
        targetNode.segmentId === 2 &&
        narrativeState.currentSegmentId !== 2 &&
        isSegmentComplete(narrativeState, '1') &&
        !hasBPlotBeenCompleted
      ) {
        setPendingNodeId(nodeId); // Store the node we want to navigate to
        setShowBPlotModal(true);
        return; // Don't navigate yet
      }

      // Batch navigation updates to prevent race conditions
      requestAnimationFrame(() => {
        setCurrentNode(nodeId);

        if (targetNode) {
          let effectiveSegmentId = targetNode.segmentId;

          // For world-variant nodes, ensure we're in the correct segment
          const worldFromNodeId = getWorldFromNodeId(nodeId);
          if (worldFromNodeId) {
            effectiveSegmentId = worldFromNodeId === 'good' ? '3A' : '3B';
          }

          if (effectiveSegmentId !== narrativeState.currentSegmentId) {
            setCurrentSegment(effectiveSegmentId);
          }
        }

        visitNode(nodeId);
      });
    },
    [
      hasBPlotBeenCompleted,
      narrativeState,
      setCurrentNode,
      setCurrentSegment,
      visitNode,
      setShowBPlotModal,
      setShowWordLimitWarning,
    ]
  );

  // Handle restart flow
  const handleRestartFlow = useCallback(() => {
    clearAllState();
    window.location.href = '/';
  }, [clearAllState]);

  // Handle B-Plot modal completion
  const handleBPlotComplete = useCallback(() => {
    // Batch all related state updates to prevent race conditions
    const targetNodeId = pendingNodeId || getSegmentStartNode(2);

    // Use requestAnimationFrame to ensure DOM is ready and batch updates properly
    requestAnimationFrame(() => {
      setHasBPlotBeenCompleted(true);
      setHasSeenBPlotIntro(true);

      // Use setTimeout to ensure B-Plot state is set before navigation
      setTimeout(() => {
        setCurrentSegment(2);
        setCurrentNode(targetNodeId);
        visitNode(targetNodeId);
        setPendingNodeId(null);
      }, 0);
    });
  }, [
    pendingNodeId,
    setCurrentSegment,
    setCurrentNode,
    visitNode,
    setHasBPlotBeenCompleted,
    setHasSeenBPlotIntro,
  ]);

  const handleHistoryBack = useCallback(() => {
    const nodeId = historyBack();
    if (nodeId && narrativeState) {
      const previousNode = narrativeState?.nodes?.[nodeId];
      const currentNode = narrativeState?.nodes?.[narrativeState.currentNodeId];

      // Check if we're going back from Segment 2 to another segment
      if (previousNode && currentNode?.segmentId === 2 && previousNode.segmentId !== 2) {
        // Restore the history position since we're intercepting
        historyForward();
        setShowBPlotModal(true);
        return;
      }

      setCurrentNode(nodeId);
      if (previousNode && previousNode.segmentId !== narrativeState.currentSegmentId) {
        setCurrentSegment(previousNode.segmentId);
      }
    }
  }, [
    historyBack,
    historyForward,
    narrativeState,
    setCurrentNode,
    setCurrentSegment,
    setShowBPlotModal,
  ]);

  const handleHistoryForward = useCallback(() => {
    const nodeId = historyForward();
    if (nodeId && narrativeState) {
      const nextNode = narrativeState?.nodes?.[nodeId];
      const currentNode = narrativeState?.nodes?.[narrativeState.currentNodeId];

      // Check if we're going forward to Segment 2 from another segment
      if (
        nextNode?.segmentId === 2 &&
        currentNode?.segmentId !== 2 &&
        isSegmentComplete(narrativeState, '1')
      ) {
        // Restore the history position since we're intercepting
        historyBack();
        setShowBPlotModal(true);
        return;
      }

      setCurrentNode(nodeId);
      if (nextNode && nextNode.segmentId !== narrativeState.currentSegmentId) {
        setCurrentSegment(nextNode.segmentId);
      }
    }
  }, [
    historyForward,
    historyBack,
    narrativeState,
    setCurrentNode,
    setCurrentSegment,
    setShowBPlotModal,
  ]);

  const clearCurrentNode = useCallback(() => {
    if (narrativeState.currentNodeId) {
      const clearedNode = clearNodeContent(narrativeState, narrativeState.currentNodeId);
      updateNode(narrativeState.currentNodeId, clearedNode.nodes[narrativeState.currentNodeId]);
    }
  }, [narrativeState, updateNode]);

  const handleExport = useCallback(() => {
    const exportData = exportToJson(narrativeState, planningState);
    const seg1 = getSegmentProgress(narrativeState, 1);
    const seg2 = getSegmentProgress(narrativeState, 2);
    const seg3A = getSegmentProgress(narrativeState, '3A');
    const seg3B = getSegmentProgress(narrativeState, '3B');
    const segmentProgress = {
      totalComplete: seg1.complete + seg2.complete + seg3A.complete + seg3B.complete,
      totalNodes: seg1.total + seg2.total + seg3A.total + seg3B.total,
      segments: {
        '1': seg1,
        '2': seg2,
        '3A': seg3A,
        '3B': seg3B,
      } as Record<SegmentId, { complete: number; total: number }>,
    };

    const markdown = exportToMarkdown(exportData, {
      userEmail: user?.email || 'Unknown User',
      projectName: projects[currentProjectId || '']?.name || 'Untitled Project',
      includeBPlotSummary: hasBPlotBeenCompleted,
      segmentProgress,
    });

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
    downloadFile(markdown, `interactive-flow-export-${timestamp}.md`);
  }, [narrativeState, planningState, user, projects, currentProjectId, hasBPlotBeenCompleted]);

  const handleGoToPlanner = useCallback(() => {
    // Just switch to planning notes tab in the right panel
    setSelectedTab('planning');
  }, [setSelectedTab]);

  // Check for story completion
  useEffect(() => {
    if (!narrativeState || hasShownCompletion) return;

    // Check if all segments are complete (both 3A and 3B must be complete)
    const segment1Complete = narrativeState.segmentCompletion['1'] === 'complete';
    const segment2Complete = narrativeState.segmentCompletion['2'] === 'complete';
    const segment3AComplete = narrativeState.segmentCompletion['3A'] === 'complete';
    const segment3BComplete = narrativeState.segmentCompletion['3B'] === 'complete';
    const segment3Complete = segment3AComplete && segment3BComplete;

    if (segment1Complete && segment2Complete && segment3Complete) {
      // Delay showing the completion screen to avoid interrupting the user
      const timer = setTimeout(() => {
        setShowCompletionScreen(true);
        setHasShownCompletion(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [narrativeState, hasShownCompletion]);

  // Handle world selection from modal
  const handleWorldSelection = useCallback(
    (world: 'good' | 'bad') => {
      // Navigate to the appropriate world's first node
      const targetNodeId = world === 'good' ? 'S3A_NPC_Recall_Positive' : 'S3B_NPC_Recall_Negative';

      handleNodeNavigation(targetNodeId);

      // Reset modal state
      setShowWorldStateModal(false);
      setPendingWorldChoice(null);
      setClickedChoiceIndex(null);
    },
    [handleNodeNavigation, setShowWorldStateModal, setPendingWorldChoice, setClickedChoiceIndex]
  );

  // Determine world state and segment for theming
  const isSegment3 =
    narrativeState.currentSegmentId === '3A' || narrativeState.currentSegmentId === '3B';

  // Get world from current node ID instead of deprecated variable
  const worldFromCurrentNode = getWorldFromNodeId(narrativeState.currentNodeId);
  const isGoodWorld = worldFromCurrentNode === 'good' || narrativeState.currentSegmentId === '3A';
  const isBadWorld = worldFromCurrentNode === 'bad' || narrativeState.currentSegmentId === '3B';

  // Determine background color for left pane based on world state in segment 3
  const getLeftPaneBackgroundColor = () => {
    if (isSegment3) {
      if (isGoodWorld) {
        return 'bg-gradient-to-b from-[#0a1a0f] to-[#0f2a1a]'; // Dark greenish
      } else if (isBadWorld) {
        return 'bg-gradient-to-b from-[#1a0a0f] to-[#2a0f1a]'; // Dark reddish
      }
    }
    // Default blue-ish background
    return 'bg-gradient-to-b from-[#050614] to-[#0f1a2f]';
  };

  // Guard against undefined state after all hooks
  if (!narrativeState || !narrativeState.currentNodeId) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-b from-[#050614] to-[#0f1a2f] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#050614] to-[#0f1a2f] flex flex-col">
      {/* Header */}
      <UnifiedTopBar
        subtitle="A-B-A Structure"
        actions={
          <div className="flex items-center gap-2">
            <ViewSwitcher activeTab={selectedTab} onTabChange={setSelectedTab} />
            <span className="text-gray-600">|</span>
            <button
              onClick={handleExport}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#1a2d4d] hover:bg-[#4f46e5] text-gray-300 hover:text-white rounded transition-all duration-300 hover:scale-105 hover:shadow-[0_0_8px_rgba(79,70,229,0.3)]"
            >
              <Download className="w-3 h-3" />
              Export
            </button>
            <button
              onClick={() => setShowSettingsPopup(true)}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-[#1a2d4d] rounded transition-all duration-300 hover:scale-105"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        }
      />

      {/*
       * Subtract the header height (57px) so the editor column stretches
       * to fill the remaining space above the bottom navigation.
       */}
      <main className="flex flex-1 overflow-hidden">
        {/* Editor Panel with Segments */}
        <div
          className={`w-[40%] flex flex-col overflow-hidden border-r border-[#1a2d4d] ${getLeftPaneBackgroundColor()}`}
        >
          {/* World State Indicator for Segment 3 */}
          {isSegment3 && (isGoodWorld || isBadWorld) && (
            <div className="px-6 pt-4 pb-2">
              <div
                className={`text-center py-2 px-4 rounded-lg border ${
                  isGoodWorld
                    ? 'bg-green-900/30 border-green-600/50 text-green-200'
                    : 'bg-red-900/30 border-red-600/50 text-red-200'
                }`}
              >
                <span className="text-sm font-semibold">
                  {isGoodWorld ? 'Better World' : 'Worse World'}
                </span>
              </div>
            </div>
          )}

          <div className="flex-1 p-6 overflow-hidden flex flex-col">
            <ComponentErrorBoundary>
              <NarrativeEditor />
            </ComponentErrorBoundary>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="flex-1 bg-[#0a0f1f] overflow-hidden">
          <ComponentErrorBoundary>
            <NarrativePreview narrativeState={narrativeState} onNodeClick={handleNodeNavigation} />
          </ComponentErrorBoundary>
        </div>
      </main>

      <SettingsPopup isOpen={showSettingsPopup} onClose={() => setShowSettingsPopup(false)} />

      <BPlotModal
        isOpen={showBPlotModal}
        onClose={() => setShowBPlotModal(false)}
        onComplete={handleBPlotComplete}
        skipIntro={hasSeenBPlotIntro || hasBPlotBeenCompleted}
      />

      <WordLimitWarningModal
        isOpen={showWordLimitWarning}
        onClose={() => setShowWordLimitWarning(false)}
        wordCount={getCurrentNodeWordCount(narrativeState)}
        wordLimit={100}
      />

      <WorldStateModal
        isOpen={showWorldStateModal}
        onClose={() => setShowWorldStateModal(false)}
        onSelectWorld={handleWorldSelection}
        clickedChoiceIndex={clickedChoiceIndex}
      />

      <CompletionScreen
        isOpen={showCompletionScreen}
        onClose={() => setShowCompletionScreen(false)}
        onExport={() => {
          setShowCompletionScreen(false);
          handleExport();
        }}
        onRestart={handleRestartFlow}
      />

      <UnifiedBottomBar
        variant="editor"
        onBack={handleHistoryBack}
        onForward={handleHistoryForward}
        canBack={canHistoryBack}
        canForward={canHistoryForward}
        onUndo={undo}
        onRedo={redo}
        onClear={clearCurrentNode}
        canUndo={canUndo}
        canRedo={canRedo}
        currentSegment={narrativeState.currentSegmentId}
        segmentCompletion={narrativeState.segmentCompletion}
        narrativeState={narrativeState}
        onSegmentChange={handleSegmentChange}
        onGoToPlanner={handleGoToPlanner}
      />
    </div>
  );
}
